import { Link, useNavigate } from '@tanstack/react-router'
import type { ApiRunRow } from '../../lib/api'
import { Badge, Panel, Spinner, ErrorState, Table, type Column } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'
import { useRuns } from './useAgents'

export function RunsTab({ active }: { active: boolean }) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useRuns(active)

  if (isLoading) return <Spinner />
  if (isError) return <ErrorState message="Couldn't load runs." />

  const runs = data ?? []

  const cols: Column<ApiRunRow>[] = [
    { key: 'started', header: 'Time', render: (r) => fmtDateTime(r.started_at) },
    { key: 'agent', header: 'Agent', render: (r) => <span className="font-medium">{r.agent_name}</span> },
    { key: 'status', header: 'Status', render: (r) =>
      <Badge tone={r.status === 'failed' ? 'red' : r.status === 'running' ? 'indigo' : 'neutral'}>{r.status}</Badge> },
    { key: 'thesis', header: 'Thesis', render: (r) => r.thesis_id ? (
      <Link
        to="/theses/$thesisId"
        params={{ thesisId: r.thesis_id }}
        onClick={(e) => e.stopPropagation()}
        className="text-slate-700 hover:underline"
      >
        {r.thesis_name || r.thesis_id.slice(0, 8)}
      </Link>
    ) : <span className="text-gray-400">—</span> },
    { key: 'mode', header: 'Mode', render: (r) => r.run_mode },
  ]

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-gray-700">
        Each run is a single agent execution the orchestrator delegated from the event log. Some are
        tied to a thesis, others run standalone (e.g. cron-triggered). Open a run to inspect its spans.
      </div>
      <Panel title={`Agent runs — ${runs.length}`}>
        <Table
          columns={cols}
          rows={runs}
          empty="No runs yet."
          onRowClick={(r) => navigate({ to: '/agents/runs/$runId', params: { runId: r.run_id } })}
        />
      </Panel>
    </div>
  )
}
